import { Flex, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Importer } from '@feathr/blackbox';
import { Button, FormSummaryItem, Text } from '@feathr/components';
import { formatImportCount, hasRunFinished } from '@feathr/extender/components/ImportRunLines';

import { EXTERNAL_ID_HELP_URL } from '../DataImportsPage.utils';

interface ISecondRecordsLineProps {
  importer: Importer;
}

/**
 * How many rows created a second record because the email address was already in the account
 * under a different External ID, with the server's sentence and the help article. Contact imports
 * only: a partner has no External ID.
 */
function SecondRecordsLine({ importer }: Readonly<ISecondRecordsLineProps>): JSX.Element | null {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const count = importer.get('second_records') ?? 0;
  const message = importer.get('second_records_message');
  const hasSecondRecords =
    hasRunFinished(importer) && importer.get('kind') !== 'partner' && count > 0 && !!message;
  if (!hasSecondRecords) {
    return null;
  }

  // The sentence is the server's, and placeholder until Product and Design approve it.
  return (
    <FormSummaryItem
      label={t('Second records')}
      value={
        <Flex align={'center'} gap={theme.spacing['1']} wrap={'wrap'}>
          <Text.DefaultRegular>
            {t('{{formattedCount}} rows {{- message}}', {
              count,
              formattedCount: formatImportCount(count),
              message,
            })}
          </Text.DefaultRegular>
          <Button
            href={EXTERNAL_ID_HELP_URL}
            rel={'noopener noreferrer'}
            target={'_blank'}
            type={'link'}
          >
            {t('Learn more about External ID')}
          </Button>
        </Flex>
      }
    />
  );
}

export default observer(SecondRecordsLine);
